// W-0560 — Footer sticky com botões Cancelar Tudo / Processar (ADR-004).
import React from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { FaCheck, FaBan } from 'react-icons/fa';
import dialog from '~/Utils/dialog';
import { toast } from 'react-toastify';
import { processarChequesReapresentacao } from '~/Services/Service';
import { processarChequesSchema } from './validationSchemas';
import { mensagens } from './mensagens';

const LIMITE_LOTE = 500;

const BotoesProcessamento = ({
  geSelecionado,
  cheques,
  processando,
  setProcessando,
  setCheques,
  setResultadoProcessamento,
}) => {
  const handleCancelarTudo = async () => {
    if (cheques.length === 0) return;
    const result = await dialog.confirm({
      title: 'Cancelar carrinho',
      message: mensagens.CONFIRMAR_CANCELAR_TODOS,
    });
    if (!result || !result.proceed) return;
    setCheques([]);
  };

  const validarCarrinho = () => {
    if (!geSelecionado) {
      toast.info(mensagens.GE_OBRIGATORIA);
      return false;
    }
    if (cheques.length === 0) {
      toast.warning(mensagens.CARRINHO_VAZIO);
      return false;
    }
    if (cheques.length > LIMITE_LOTE) {
      toast.error(mensagens.LIMITE_LOTE);
      return false;
    }
    const semConta = cheques.find(
      (c) => !c.conta_deposito || c.conta_deposito <= 0
    );
    if (semConta) {
      toast.error(mensagens.CONTA_DEPOSITO_NULA);
      return false;
    }
    return true;
  };

  const enviarParaBackend = async () => {
    const payload = {
      codigo_grupo_empresa: geSelecionado.codigo,
      cheques: cheques.map((c) => ({
        codigo_titulo: c.codigo_titulo,
        conta_deposito: c.conta_deposito,
        taxa_digitada: Number(c.taxa_digitada || 0),
        juros_digitado: Number(c.juros_digitado || 0),
      })),
    };

    try {
      await processarChequesSchema.validate(payload, { abortEarly: true });
    } catch (validationErr) {
      const msg =
        validationErr && validationErr.message
          ? validationErr.message
          : mensagens.ERRO_PROCESSAMENTO;
      toast.error(msg);
      return;
    }

    setProcessando(true);
    try {
      const res = await processarChequesReapresentacao(payload);
      const processados = (res && res.processados) || [];
      const pulados = (res && res.pulados) || [];
      const falhados = (res && res.falhados) || [];

      setResultadoProcessamento({ processados, pulados, falhados });

      // Remove processados do carrinho — mantém pulados/falhados para retry.
      if (processados.length > 0) {
        setCheques((prev) =>
          prev.filter((c) => !processados.includes(c.codigo_titulo))
        );
      }

      if (processados.length > 0 && pulados.length === 0 && falhados.length === 0) {
        toast.success(
          `${mensagens.PROCESSAMENTO_OK} (${processados.length} cheque(s))`
        );
      } else if (processados.length > 0 || pulados.length > 0 || falhados.length > 0) {
        toast.warning(mensagens.PROCESSAMENTO_PARCIAL);
      }

      pulados.forEach((p) =>
        toast.info(`Cheque ${p.codigo_titulo}: ${p.motivo || 'pulado'}.`)
      );
      falhados.forEach((f) =>
        toast.error(`Cheque ${f.codigo_titulo}: ${f.erro || 'erro'}.`)
      );
    } catch (err) {
      const msg = err && err.message ? err.message : mensagens.ERRO_PROCESSAMENTO;
      toast.error(msg);
    } finally {
      setProcessando(false);
    }
  };

  const handleProcessar = async () => {
    if (!validarCarrinho()) return;

    // Confirm bloqueante se houver cheque com vencimento futuro (PRD §5.3).
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    const temVencimentoFuturo = cheques.some((c) => {
      if (!c.data_vencimento) return false;
      const d = new Date(c.data_vencimento);
      if (Number.isNaN(d.getTime())) return false;
      d.setHours(0, 0, 0, 0);
      return d > hoje;
    });

    if (temVencimentoFuturo) {
      const result = await dialog.confirm({
        title: 'Verificar vencimento',
        message: mensagens.CONFIRMAR_VENCIMENTO_FUTURO,
      });
      if (!result || !result.proceed) return;
    }

    await enviarParaBackend();
  };

  return (
    <div
      style={{
        position: 'sticky',
        bottom: 0,
        background: '#ffffff',
        borderTop: '1px solid #e0e0e0',
        padding: '12px 16px',
        zIndex: 10,
        boxShadow: '0 -2px 6px rgba(0,0,0,0.05)',
      }}
      className="d-flex justify-content-between align-items-center flex-wrap"
    >
      <div className="small text-muted">
        {cheques.length > 0
          ? `${cheques.length} cheque(s) no carrinho`
          : 'Carrinho vazio'}
      </div>
      <div>
        <Button
          variant="outline-secondary"
          size="sm"
          className="mr-2"
          onClick={handleCancelarTudo}
          disabled={processando || cheques.length === 0}
        >
          <FaBan className="mr-1" /> Cancelar tudo
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={handleProcessar}
          disabled={processando || cheques.length === 0 || !geSelecionado}
        >
          {processando ? (
            <>
              <Spinner animation="border" size="sm" className="mr-1" />
              Processando...
            </>
          ) : (
            <>
              <FaCheck className="mr-1" /> Processar
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default BotoesProcessamento;
